"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cn } from "@/lib/utils"
import { LucideIcon } from "lucide-react"

/**
 * Button Variants Guide:
 *
 * - primary:     Blue background, white text. Use for THE main action per view.
 *                Only ONE primary button should be visible at a time.
 *
 * - default:     White/light background, dark text. High contrast, secondary emphasis.
 *
 * - secondary:   Dark with border. For less important actions alongside primary.
 *
 * - outline:     Transparent with border. For tertiary actions.
 *
 * - ghost:       No background. For toolbar actions, icon buttons, subtle actions.
 *
 * - destructive: Red background. For delete, remove, cancel dangerous actions.
 *
 * - glass:       Glassmorphic effect. For marketing pages and overlays.
 *
 * - link:        Text only with underline. For inline text actions.
 */

type ButtonVariant =
  | "primary"
  | "default"
  | "secondary"
  | "outline"
  | "ghost"
  | "destructive"
  | "glass"
  | "link"

type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl" | "icon"

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  variant?: ButtonVariant
  size?: ButtonSize
  /** Left icon */
  leftIcon?: LucideIcon
  /** Right icon */
  rightIcon?: LucideIcon
  /** Loading state */
  loading?: boolean
  children?: React.ReactNode
}

const baseClasses =
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 " +
  "focus:outline-none focus:ring-2 focus:ring-[var(--supreme-primary)] focus:ring-opacity-50 focus:ring-offset-2 focus:ring-offset-dark-primary " +
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--supreme-primary)] text-white hover:brightness-110 active:brightness-95 shadow-sm",
  default:
    "bg-white text-dark-primary hover:bg-gray-100 active:bg-gray-200",
  secondary:
    "bg-dark-secondary bg-opacity-70 backdrop-blur-sm border border-dark-border " +
    "text-white hover:border-white/50 hover:bg-dark-accent/50",
  outline:
    "border border-dark-border text-white bg-transparent " +
    "hover:bg-dark-secondary hover:bg-opacity-50",
  ghost:
    "text-dark-text-secondary bg-transparent " +
    "hover:text-white hover:bg-dark-accent/50",
  destructive:
    "bg-[var(--supreme-error)] text-white hover:brightness-110 active:brightness-95",
  glass:
    "glass-button text-white hover:scale-105",
  link:
    "text-[var(--supreme-primary)] underline-offset-4 hover:underline bg-transparent p-0 h-auto",
}

const sizes: Record<ButtonSize, string> = {
  xs: "h-7 px-2 text-xs",
  sm: "h-8 px-3 text-sm",
  md: "h-9 px-4 text-sm",
  lg: "h-10 px-5 text-base",
  xl: "h-12 px-6 text-base",
  icon: "h-9 w-9 p-0",
}

const iconSizes: Record<ButtonSize, string> = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-4 w-4",
  xl: "h-5 w-5",
  icon: "h-4 w-4",
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "default",
      size = "md",
      asChild = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      loading = false,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    const iconClass = iconSizes[size]

    return (
      <Comp
        ref={ref}
        className={cn(
          baseClasses,
          variants[variant],
          sizes[size],
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span
            className={cn(
              "animate-spin rounded-full border-2 border-current border-t-transparent",
              iconClass
            )}
          />
        ) : LeftIcon ? (
          <LeftIcon className={iconClass} />
        ) : null}

        {children}

        {!loading && RightIcon && (
          <RightIcon className={iconClass} />
        )}
      </Comp>
    )
  }
)

Button.displayName = "Button"

/**
 * Button Group - for related actions
 */
interface ButtonGroupProps {
  children: React.ReactNode
  /** Attached buttons (no gap, shared borders) */
  attached?: boolean
  className?: string
}

export function ButtonGroup({ children, attached = false, className }: ButtonGroupProps) {
  return (
    <div
      className={cn(
        "inline-flex",
        attached
          ? "[&>button]:rounded-none [&>button:first-child]:rounded-l-lg [&>button:last-child]:rounded-r-lg [&>button:not(:last-child)]:border-r-0"
          : "gap-2",
        className
      )}
    >
      {children}
    </div>
  )
}
